"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const Error_1 = __importDefault(require("@mui/icons-material/Error"));
const material_1 = require("@mui/material");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const axios_1 = require("axios");
const pino_1 = __importDefault(require("pino"));
const react_1 = require("react");
const react_router_1 = require("react-router");
const zod_1 = require("zod");
const strings_1 = require("../../../../constants/strings");
const uiConstants_1 = require("../../../../constants/uiConstants");
const sessionQuery_1 = require("../../../../queries/sessionQuery");
const routerUtils_1 = require("../../../../router/routerUtils");
const zustandStore_1 = require("../../../../zustandStore");
const banner_1 = require("../common/banner");
const title_1 = require("../common/title");
const authForm_1 = __importDefault(require("../loginform/authForm"));
const useEmailInput_1 = require("../loginform/hooks/useEmailInput");
const lexico = new lexico_1.default();
const logger = (0, pino_1.default)();
const forgotPasswordSchema = zod_1.z.object({
    email: zod_1.z.email(),
});
const ForgotPassword = () => {
    const theme = (0, material_1.useTheme)();
    const navigate = (0, react_router_1.useNavigate)();
    const { setNotification } = (0, zustandStore_1.useZustand)(state => ({
        setNotification: state.setNotification,
    }));
    const { navigateToLockedAccountMessage } = (0, routerUtils_1.useSigninMessageNavigation)();
    const { state: emailState, handleEmailChange, handleEmailValidation } = (0, useEmailInput_1.useEmailInput)();
    const isFormValid = forgotPasswordSchema.safeParse({ email: emailState.value }).success;
    const forgotPassword = (0, sessionQuery_1.useForgotPasswordQuery)();
    const [isFormLoading, setIsFormLoading] = (0, react_1.useState)(false);
    const [emailError, setEmailError] = (0, react_1.useState)('');
    const handleForgotPasswordError = (error) => {
        if (!(0, axios_1.isAxiosError)(error)) {
            setNotification([
                {
                    type: strings_1.notificationStrings.WARNING,
                    message: lexico.trc('Warning message', 'An error occurred, please try again later.'),
                },
            ]);
            return;
        }
        const errorStatus = error.response?.status;
        const errorCode = error.response?.data?.errorCode;
        if (globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP) {
            if (errorStatus === 403 && (errorCode === 'LOCKED_ACCOUNT' || !errorCode)) {
                logger.warn('Account locked due to multiple failed attempts', { errorCode });
                navigateToLockedAccountMessage('password');
                return;
            }
            if (errorStatus === 403 && errorCode === 'INCOMPLETE_ACTIVATION') {
                logger.warn('Account activation is incomplete', { errorCode });
                navigateToLockedAccountMessage('accountNotCreated');
                return;
            }
            if (errorStatus === 404 || errorStatus === 401) {
                setEmailError(lexico.tr('The email address you entered is not registered'));
                return;
            }
        }
        setNotification([
            {
                type: strings_1.notificationStrings.WARNING,
                message: lexico.trc('Warning message', 'An error occurred, please try again later.'),
            },
        ]);
    };
    const handleSubmit = (event) => {
        event.preventDefault();
        setIsFormLoading(true);
        setEmailError('');
        forgotPassword.mutate({ username: emailState.value }, {
            onSuccess: async () => {
                void navigate('/signin/message', {
                    state: {
                        heading: lexico.trc('Component title', 'New password requested'),
                        message: `If ${emailState.value} matched an existing account, we'll send an email with details on how to proceed. If after a few minutes you still cannot see the email in your inbox please check your spam folder.`,
                    },
                });
                setIsFormLoading(false);
            },
            onError: error => {
                setIsFormLoading(false);
                handleForgotPasswordError(error);
            },
        });
    };
    return ((0, jsx_runtime_1.jsx)(authForm_1.default, { children: (0, jsx_runtime_1.jsxs)(material_1.Container, { style: {
                width: '360px',
                padding: 0,
                border: `1px solid ${theme.palette.tsGoColor.grayLightest}`,
                backgroundColor: theme.palette.tsGoColor.white,
                marginTop: theme?.tsGoUnit.tsUnitHalf,
            }, children: [(0, jsx_runtime_1.jsx)(banner_1.Banner, { className: "banner signin" }), (0, jsx_runtime_1.jsx)(title_1.Title, { children: lexico.trc('Component title', 'Forgot Password') }), (0, jsx_runtime_1.jsxs)("form", { "data-testid": "form", style: {
                        padding: `0 ${theme.tsGoUnit.tsUnit} ${theme.tsGoUnit.tsUnit}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: theme.tsGoUnit.tsUnit,
                    }, onSubmit: handleSubmit, children: [(0, jsx_runtime_1.jsxs)(material_1.FormControl, { style: { padding: 0 }, fullWidth: true, variant: "outlined", error: emailState.isError || !!emailError, children: [(0, jsx_runtime_1.jsx)(material_1.TextField, { "data-testid": "email", label: lexico.tr('Email'), style: { padding: 0, margin: 0 }, type: "email", required: true, fullWidth: true, margin: "normal", variant: "outlined", autoComplete: "off", value: emailState.value, error: emailState.isError || !!emailError, onChange: e => {
                                        handleEmailChange(e.target.value);
                                        setEmailError('');
                                    }, onBlur: handleEmailValidation, slotProps: {
                                        htmlInput: {
                                            'data-testid': 'email',
                                        },
                                        inputLabel: {
                                            style: { fontWeight: theme.typography.tsFontWeight.default },
                                        },
                                    } }), emailState.isError && ((0, jsx_runtime_1.jsxs)(material_1.FormHelperText, { style: { marginLeft: 0 }, children: [(0, jsx_runtime_1.jsx)(Error_1.default, { fontSize: "small", style: { verticalAlign: 'middle' } }), " ", lexico.tr('Invalid email address')] })), emailError && ((0, jsx_runtime_1.jsxs)(material_1.FormHelperText, { error: true, style: {
                                        marginLeft: 0,
                                        color: theme.palette.error.main,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '4px',
                                    }, children: [(0, jsx_runtime_1.jsx)(Error_1.default, { fontSize: "small", style: { marginTop: '2px' } }), (0, jsx_runtime_1.jsx)("span", { children: emailError })] }))] }), (0, jsx_runtime_1.jsx)(material_1.Button, { style: {
                                width: '100%',
                                fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
                            }, "data-testid": "submit", color: uiConstants_1.UI_BUTTON_PRIMARY, disabled: !isFormValid, type: "submit", loading: isFormLoading, loadingPosition: "start", startIcon: (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {}), variant: "contained", children: lexico.trc('Form input label', 'Send Instructions') })] })] }) }));
};
exports.default = ForgotPassword;
